import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  TextField,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";
import { useTranslation } from "react-i18next";

const validationSchema = yup.object().shape({
  first_name: yup.string().required("Name is required"),
  last_name: yup.string().required("Last name is required"),
  skill_level: yup.number().min(0).max(2).required("Skill level is required"),
  status: yup.string().required("Status is required"),
});

const DialogEditUser = ({ open, onClose, user, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  console.log("user: ", user);


  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      skill_level: 0,
      status: "0",
    },
  });

  useEffect(() => {
    if (user && open) {
      reset({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        skill_level: user.skill_level || 0,
        status: user.status || "0",
      });
    }
  }, [user, open, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Remove email from payload since it's not editable
      const { email, ...payload } = data;
      const response = await axiosInstance.patch(
        ApiEndPoints.USERS.update(user.id),
        payload
      );
      toastSuccess(response.data?.message || "User updated successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "400px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("Edit User")}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormLabel
                htmlFor="first_name"
                style={{ fontSize: "12px", color: "#6C7278", fontWeight: 500 }}
              >
                {t("Full Name")}
              </FormLabel>
              <Controller
                name="first_name"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    autoFocus
                    onChange={onChange}
                    id="first_name"
                    value={value}
                    placeholder={t("Enter Full Name")}
                    sx={{
                      backgroundColor: "#F7F7F7",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#EDF1F3",
                          borderWidth: "1px",
                        },
                        "&:hover fieldset": {
                          borderColor: "#EDF1F3",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#EDF1F3",
                        },
                      },
                      // Placeholder styling
                      "& .MuiInputBase-input::placeholder": {
                        color: "#777777",
                        fontWeight: 500,
                        fontSize: "16px",
                        opacity: 1,
                      },
                    }}
                    disabled={loading}
                  />
                )}
              />
              {errors.first_name && (
                <FormHelperText sx={{ color: "error.main" }}>
                  {errors.first_name.message}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormLabel
                htmlFor="last_name"
                style={{ fontSize: "12px", color: "#6C7278", fontWeight: 500 }}
              >
                {t("Last Name")}
              </FormLabel>
              <Controller
                name="last_name"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    autoFocus
                    onChange={onChange}
                    id="last_name"
                    value={value}
                    placeholder={t("Enter Last Name")}
                    sx={{
                      backgroundColor: "#F7F7F7",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#EDF1F3",
                          borderWidth: "1px",
                        },
                        "&:hover fieldset": {
                          borderColor: "#EDF1F3",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#EDF1F3",
                        },
                      },
                      // Placeholder styling
                      "& .MuiInputBase-input::placeholder": {
                        color: "#777777",
                        fontWeight: 500,
                        fontSize: "16px",
                        opacity: 1,
                      },
                    }}
                    disabled={loading}
                  />
                )}
              />
              {errors.last_name && (
                <FormHelperText sx={{ color: "error.main" }}>
                  {errors.last_name.message}
                </FormHelperText>
              )}
            </FormControl>

        

            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormLabel
                htmlFor="skill_level"
                style={{ fontSize: "12px", color: "#6C7278", fontWeight: 500 }}
              >
                {t("Skill Level")}
              </FormLabel>
              <Controller
                name="skill_level"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value ?? 0}
                    onChange={onChange}
                    disabled={loading}
                    sx={{
                      backgroundColor: "#F7F7F7",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#EDF1F3",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#EDF1F3",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#EDF1F3",
                      },
                    }}
                  >
                    <MenuItem value={0}>{t("Beginner")}</MenuItem>
                    <MenuItem value={1}>{t("Intermediate")}</MenuItem>
                    <MenuItem value={2}>{t("Advanced")}</MenuItem>
                  </Select>
                )}
              />
              {errors.skill_level && (
                <FormHelperText sx={{ color: "error.main" }}>
                  {errors.skill_level.message}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormLabel
                htmlFor="status"
                style={{ fontSize: "12px", color: "#6C7278", fontWeight: 500 }}
              >
                {t("Status")}
              </FormLabel>
              <Controller
                name="status"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value ?? "0"}
                    onChange={onChange}
                    disabled={loading}
                    sx={{
                      backgroundColor: "#F7F7F7",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#EDF1F3",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#EDF1F3",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#EDF1F3",
                      },
                    }}
                  >
                    <MenuItem value="0">{t("Active")}</MenuItem>
                    <MenuItem value="1">{t("Inactive")}</MenuItem>
                    <MenuItem value="2">{t("Pending")}</MenuItem>
                  </Select>
                )}
              />
              {errors.status && (
                <FormHelperText sx={{ color: "error.main" }}>
                  {errors.status.message}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ color: "text.secondary" }}
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: "#17D96C",
            "&:hover": {
              backgroundColor: "#15C55D",
            },
          }}
        >
          {loading ? t("Updating...") : t("Update User")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogEditUser;
